import React, { useState, useEffect } from 'react';
import { Container, Typography, Box, useTheme, Paper, Fade } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import IdeaForm from '../components/IdeaForm';
import { Idea } from '../types';
import { api } from '../services/api';
import { useTenant } from '../contexts/TenantContext';
import { SubmitContent, contentService } from '../services/content';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';

// Add custom animation styles
const customStyles = `
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes gradientBg {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}
`;

const IdeaSubmission: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { tenantConfig } = useTenant();
  const [animateIn, setAnimateIn] = useState(false);
  const [submitContent, setSubmitContent] = useState<SubmitContent | null>(null);

  // Load tenant-specific content
  useEffect(() => {
    const loadContent = async () => {
      try {
        const content = await contentService.getSubmitContent(tenantConfig?.tenant_id);
        setSubmitContent(content);
      } catch (error) {
        console.error('Failed to load submit content:', error);
      }
    };

    loadContent();
  }, [tenantConfig?.tenant_id]);

  // Animate in components after initial render
  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimateIn(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = async (
    idea: Omit<Idea, 'id' | 'upvotes' | 'commentsCount' | 'created_at' | 'likes'>, 
    imageFile?: File
  ) => {
    try {
      console.log('Submitting idea:', idea);
      // First create the idea
      const newIdea = await api.addIdea(idea);
      
      // If an image was provided, upload it
      if (imageFile && newIdea.id) {
        console.log('Uploading image for idea:', newIdea.id);
        await api.uploadIdeaImage(newIdea.id, imageFile);
      }
      
      // Navigate to the newly created idea's detail page
      navigate(`/idea/${newIdea.id}`);
    } catch (err) {
      console.error('Error submitting idea:', err);
      throw err; // Re-throw to let the form component handle the error display
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <style>{customStyles}</style>
      
      <Fade in={animateIn} timeout={800}>
        <Box sx={{ mb: 5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <AddCircleOutlineIcon 
              color="secondary" 
              sx={{ 
                fontSize: '2rem', 
                mr: 1.5,
                animation: 'pulse 2s infinite'
              }} 
            />
            <Typography 
              variant="h4" 
              component="h1" 
              sx={{ 
                fontWeight: 700,
                position: 'relative',
                display: 'inline-block',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -8,
                  left: 0,
                  width: 60,
                  height: 3,
                  backgroundColor: 'secondary.main',
                }
              }}
            >
              {submitContent?.header.title || 'Submit Your Idea'}
            </Typography>
          </Box>
          <Typography 
            variant="h6" 
            color="text.secondary" 
            sx={{ 
              mt: 2, 
              fontWeight: 400, 
              lineHeight: 1.5,
              maxWidth: '800px'
            }}
          >
            {submitContent?.header.subtitle || 'Share your vision for the future of our community space.'}
          </Typography>
        </Box>
      </Fade>

      <Fade in={animateIn} timeout={1000} style={{ transitionDelay: '200ms' }}>
        <Box>
          <Paper 
            elevation={3} 
            sx={{ 
              p: { xs: 2, md: 4 }, 
              borderRadius: 2,
              background: 'white',
              position: 'relative',
              overflow: 'hidden',
              boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
              '&::after': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '4px',
                background: (theme) => 
                  `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              }
            }}
          >
            <IdeaForm onSubmit={handleSubmit} />
          </Paper>
        </Box>
      </Fade>
      
      <Fade in={animateIn} timeout={1000} style={{ transitionDelay: '400ms' }}>
        <Box sx={{ mt: 5 }}>
          <Paper 
            sx={{ 
              p: 4, 
              bgcolor: 'rgba(63, 81, 181, 0.04)', 
              borderRadius: 2,
              position: 'relative',
              overflow: 'hidden',
              background: 'linear-gradient(120deg, rgba(63, 81, 181, 0.05), rgba(156, 39, 176, 0.05))',
              backgroundSize: '200% 200%',
              animation: 'gradientBg 15s ease infinite',
              boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
              border: '1px solid rgba(63, 81, 181, 0.1)'
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
              {submitContent?.projectInfo.title || 'About This Project'}
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.primary', lineHeight: 1.7 }}>
              {submitContent?.projectInfo.description || 'A community-driven initiative to transform our space.'}
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              mt: 3, 
              gap: 2,
              justifyContent: { xs: 'center', sm: 'flex-start' }
            }}>
              <Box sx={{ 
                p: 2, 
                borderRadius: 2, 
                bgcolor: 'rgba(255,255,255,0.7)', 
                flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 16px)' },
                maxWidth: { xs: '100%', sm: 'calc(50% - 16px)' },
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 6px 16px rgba(0,0,0,0.1)',
                }
              }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: 'primary.dark' }}>
                  {submitContent?.projectInfo.lookingFor.title || 'What We\'re Looking For'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {submitContent?.projectInfo.lookingFor.description || 'Creative ideas for public spaces and community features.'}
                </Typography>
              </Box>
              <Box sx={{ 
                p: 2, 
                borderRadius: 2, 
                bgcolor: 'rgba(255,255,255,0.7)', 
                flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 16px)' },
                maxWidth: { xs: '100%', sm: 'calc(50% - 16px)' },
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 6px 16px rgba(0,0,0,0.1)',
                }
              }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: 'primary.dark' }}>
                  {submitContent?.projectInfo.impact.title || 'Your Impact'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {submitContent?.projectInfo.impact.description || 'Community input is essential to creating a space that reflects local needs.'}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Fade>
    </Container>
  );
};

export default IdeaSubmission; 